import React, { useMemo } from 'react';
import { Column, Line } from '@ant-design/plots';
import { Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { resolveBenchmarkRangesForMetric } from '../../helpers/metricRules';
import { resolveBenchmarkBand } from './benchmarkUtils';

const { Text } = Typography;

type ChartType = 'line' | 'bar';

interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number;
    dateRange?: [Dayjs, Dayjs];
}

type PlotPoint = {
    x: string;
    series: string;
    value: number | null;
};

const KPI_FIELD = 'average';

const toDayKey = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    const parsed = dayjs(value as any);
    if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length >= 10) return trimmed.slice(0, 10);
    }
    return null;
};

const avg = (values: number[]): number => {
    if (!values.length) return 0;
    return values.reduce((sum, current) => sum + current, 0) / values.length;
};

const buildDayRange = (range?: [Dayjs, Dayjs]): string[] => {
    if (!range || range.length !== 2) return [];
    const start = range[0]?.startOf('day');
    const end = range[1]?.startOf('day');
    if (!start || !end || !start.isValid() || !end.isValid() || start.isAfter(end)) return [];

    const days: string[] = [];
    let cursor = start;
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
        days.push(cursor.format('YYYY-MM-DD'));
        cursor = cursor.add(1, 'day');
    }
    return days;
};

export const ReportDailyKpiHistory: React.FC<CommonSectionProps> = ({
    section,
    data,
    areas,
    metrics,
    previewAreaId,
    dateRange,
}) => {
    const chartType: ChartType = section.chartType === 'bar' ? 'bar' : 'line';
    const requestedShowParent = section.showParent !== false;
    const showChildren = section.showChildren === true;
    const showParent = requestedShowParent || !showChildren;
    const selectedAreaName = useMemo(
        () => areas.find((area: any) => area?.id === previewAreaId)?.name || null,
        [areas, previewAreaId]
    );
    const title = section?.title || 'Daily KPI history';
    const viewLabel = showChildren ? 'Compare areas' : 'Selected area';
    const chartLabel = chartType === 'bar' ? 'Bars' : 'Line';

    const { points, hasValues } = useMemo(() => {
        const metricLookup = new Map<string, any>();
        metrics.forEach((metric: any) => {
            if (metric?.key) metricLookup.set(metric.key, metric);
        });

        const areaLookup = new Map<number, any>();
        areas.forEach((area: any) => {
            if (area?.id) areaLookup.set(area.id, area);
        });

        const scoreByDayArea = new Map<string, number[]>();
        const daySet = new Set<string>();

        const metricSeries = Array.isArray(data) ? data : [];
        metricSeries.forEach((entry: any) => {
            const metricKey = entry?.metricKey;
            const metricConfig = metricLookup.get(metricKey);
            const pointList = Array.isArray(entry?.points) ? entry.points : [];

            pointList.forEach((point: any) => {
                const dayKey = toDayKey(point?.x ?? point?.bucket);
                if (!dayKey) return;

                const areaId = Number(point?.areaId);
                const resolvedAreaId =
                    Number.isFinite(areaId) && areaId > 0 ? areaId : Number(previewAreaId);
                if (!Number.isFinite(resolvedAreaId) || resolvedAreaId <= 0) return;

                const rawValue = point?.[KPI_FIELD] ?? point?.avg;
                const numericValue =
                    typeof rawValue === 'number' ? rawValue : Number(rawValue);
                if (!Number.isFinite(numericValue)) return;

                const targetArea = areaLookup.get(resolvedAreaId) || areaLookup.get(previewAreaId);
                const benchmarkRanges = resolveBenchmarkRangesForMetric(metricConfig, targetArea);
                if (!benchmarkRanges) return;

                const benchmarkBand = resolveBenchmarkBand(numericValue, benchmarkRanges);
                if (!benchmarkBand) return;

                const key = `${dayKey}::${resolvedAreaId}`;
                const list = scoreByDayArea.get(key) || [];
                list.push(benchmarkBand.score);
                scoreByDayArea.set(key, list);
                daySet.add(dayKey);
            });
        });

        const areaDayScore = new Map<string, number>();
        const dailyByArea = new Map<string, Map<number, number>>();
        scoreByDayArea.forEach((scores, key) => {
            const [dayKey, areaIdRaw] = key.split('::');
            const areaId = Number(areaIdRaw);
            if (!dayKey || !Number.isFinite(areaId) || scores.length === 0) return;
            const score = Number(avg(scores).toFixed(2));
            areaDayScore.set(key, score);
            if (!dailyByArea.has(dayKey)) dailyByArea.set(dayKey, new Map<number, number>());
            dailyByArea.get(dayKey)!.set(areaId, score);
        });

        const rangeDays = buildDayRange(dateRange);
        const dayKeys = rangeDays.length > 0 ? rangeDays : Array.from(daySet).sort();
        const allAreaIds = Array.from(
            new Set(
                Array.from(dailyByArea.values()).flatMap((map) => Array.from(map.keys()))
            )
        ).sort((a, b) => {
            const aName = areaLookup.get(a)?.name || `Area ${a}`;
            const bName = areaLookup.get(b)?.name || `Area ${b}`;
            return aName.localeCompare(bName);
        });

        const rows: PlotPoint[] = [];
        const parentLabel = `${areaLookup.get(previewAreaId)?.name || 'Selected area'} (avg)`;

        if (showParent) {
            dayKeys.forEach((dayKey) => {
                const dayMap = dailyByArea.get(dayKey);
                const values = dayMap ? Array.from(dayMap.values()) : [];
                rows.push({
                    x: dayKey,
                    series: parentLabel,
                    value: values.length > 0 ? Number(avg(values).toFixed(2)) : null,
                });
            });
        }

        if (showChildren) {
            allAreaIds.forEach((areaId) => {
                const areaName = areaLookup.get(areaId)?.name || `Area ${areaId}`;
                dayKeys.forEach((dayKey) => {
                    const score = dailyByArea.get(dayKey)?.get(areaId);
                    rows.push({
                        x: dayKey,
                        series: areaName,
                        value: score !== undefined ? score : null,
                    });
                });
            });
        }

        return {
            points: rows,
            hasValues: rows.some((row) => typeof row.value === 'number' && Number.isFinite(row.value)),
        };
    }, [areas, data, dateRange, metrics, previewAreaId, showChildren, showParent]);

    if (!hasValues) {
        return (
            <div className="report-history-surface">
                <div className="report-history-head">
                    <div className="report-history-title">{title}</div>
                    {selectedAreaName ? <div className="report-history-area">{selectedAreaName}</div> : null}
                </div>
                <div className="report-history-meta">
                    <span className="report-history-pill">KPI / 5</span>
                    <span className="report-history-pill">{viewLabel}</span>
                    <span className="report-history-pill report-history-pill--muted">{chartLabel}</span>
                </div>
                <div className="report-history-empty">
                    <Text type="secondary">No benchmark KPI history available for this period.</Text>
                </div>
            </div>
        );
    }

    const commonConfig = {
        data: points,
        xField: 'x',
        yField: 'value',
        seriesField: 'series',
        legend: { color: { position: 'top' as const } },
        axis: {
            y: { title: 'Daily total KPI (1-5)' },
            x: {
                title: false,
                label: { autoHide: true, autoRotate: true },
            },
        },
        scale: {
            x: { type: 'cat' as const },
            y: { min: 1, max: 5, nice: false },
        },
        tooltip: {
            items: [
                {
                    channel: 'y',
                    valueFormatter: (value: any) =>
                        value === null || value === undefined || Number.isNaN(Number(value))
                            ? 'No data'
                            : `${Number(value).toFixed(2)} / 5`,
                },
            ],
        },
        animation: false,
        height: 248,
    };

    return (
        <div className="report-history-surface">
            <div className="report-history-head">
                <div className="report-history-title">{title}</div>
                {selectedAreaName ? <div className="report-history-area">{selectedAreaName}</div> : null}
            </div>
            <div className="report-history-meta">
                <span className="report-history-pill">KPI / 5</span>
                <span className="report-history-pill">{viewLabel}</span>
                <span className="report-history-pill report-history-pill--muted">{chartLabel}</span>
            </div>
            <div className="report-history-frame">
                {chartType === 'bar' ? (
                    <Column
                        {...commonConfig}
                        isGroup
                        label={{
                            position: 'top',
                            formatter: (datum: any) =>
                                datum?.value === null || datum?.value === undefined
                                    ? ''
                                    : `${Number(datum.value).toFixed(2)}`,
                        }}
                    />
                ) : (
                    <Line
                        {...commonConfig}
                        point={{ size: 3, shape: 'circle' }}
                        connectNulls={false}
                        smooth={false}
                    />
                )}
            </div>
        </div>
    );
};

export default ReportDailyKpiHistory;
